import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate, useParams, useRouter } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import EventForm from '@web/src/features/event/event-form/event-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { EventInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  EditEventByIdQuery,
  EditEventByIdQueryVariables,
  UpdateEventInput,
  UpdateEventMutation,
  UpdateEventMutationVariables,
} from 'types/graphql'

const EDIT_EVENT_QUERY: TypedDocumentNode<EditEventByIdQuery, EditEventByIdQueryVariables> = gql`
  query EditEventById($eventIdOrSlug: String!) {
    event: event(eventIdOrSlug: $eventIdOrSlug) {
      ...EventInfo
    }
    eventTags {
      id
      name
    }
  }
  ${EventInfo}
`

const UPDATE_EVENT_MUTATION: TypedDocumentNode<UpdateEventMutation, UpdateEventMutationVariables> = gql`
  mutation UpdateEventMutation($eventIdOrSlug: String!, $input: UpdateEventInput!) {
    updateEvent(eventIdOrSlug: $eventIdOrSlug, input: $input) {
      ...EventInfo
    }
  }
  ${EventInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/event/$eventIdOrSlug_/edit')({
  component: EditEventPage,
  head: () =>
    seo({
      title: `Edit Event | ${env.VITE_APP_NAME}`,
      description: 'Edit a Event entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_EVENT_QUERY, {
      variables: { eventIdOrSlug: params.eventIdOrSlug },
    })
    return { queryRef }
  },
})

function EditEventPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const params = useParams({ strict: false })
  const { eventIdOrSlug } = Route.useParams()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditEventByIdQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updateEvent] = useMutation(UPDATE_EVENT_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Event updated'))
      navigate({
        to: '/event/$eventIdOrSlug',
        params: { eventIdOrSlug: result.updateEvent.slug || result.updateEvent.id },
      } as never)
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: UpdateEventInput) => {
    setShowSpinnerValue(true)

    updateEvent({
      variables: {
        eventIdOrSlug: data?.event?.slug || data?.event?.id || eventIdOrSlug,
        input: input,
      },
    })
  }

  const handleCancel = () => {
    router.history.back()
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.event) {
    return <ErrorAlert error={new Error(t('Event not found'))} />
  }

  if (!data?.eventTags) {
    return <ErrorAlert error={new Error(t('Event tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pt-4">
      <div className="flex w-full flex-col gap-4">
        <SFBackPageHeader lowerText={data.event.name} onBackClick={handleCancel} title={t('Edit Event')} />
        <EventForm
          event={data.event}
          eventTags={data.eventTags}
          groupId={params?.groupId}
          onSave={onSave}
          showSpinner={showSpinner}
        />
      </div>
    </div>
  )
}
